import Link from "next/link";
import React from "react";

interface FeaturesProps {
  eyebrow: string;
  title: string;
  description: string;
  id?: string;
  children: React.ReactNode;
}

export function Features({
  eyebrow,
  title,
  description,
  children,
}: Readonly<FeaturesProps>) {
  return (
    <div className="bg-white py-24 sm:py-32 dark:bg-slate-900">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base leading-7 font-semibold text-purple-600 dark:text-purple-400">
            {eyebrow}
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
            {title}
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
            {description}
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
            {children}
          </dl>
        </div>
      </div>
    </div>
  );
}

interface FeatureItemProps {
  name: string;
  description: string;
  children: React.ReactNode;
  href?: string;
}

export function FeatureItem({
  name,
  description,
  children,
  href,
}: Readonly<FeatureItemProps>) {
  const content = (
    <div className="relative pl-16">
      <dt className="text-base leading-7 font-semibold text-gray-900 dark:text-white">
        <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-600">
          {children}
        </div>
        {name}
      </dt>
      <dd className="mt-2 text-base leading-7 text-gray-600 dark:text-gray-300">
        {description}
      </dd>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="group">
        <div className="hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg p-4 -m-4 transition-colors">
          <div className="relative pl-16">
            <dt className="text-base leading-7 font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
              <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-600 group-hover:bg-purple-700 transition-colors">
                {children}
              </div>
              {name}
            </dt>
            <dd className="mt-2 text-base leading-7 text-gray-600 dark:text-gray-300">
              {description}
            </dd>
          </div>
        </div>
      </Link>
    );
  }

  return content;
}
